/*
 * userProfilingReducer
 * Description
 * The reducer takes care of our data. Using actions, we can
 * update our application state. To add a new action,
 * add it to the switch statement in the reducer function
 *
 * @version 1.0.0
 * @author NEOSYN\user
 * @since 18-Oct-2024
 */

import { produce } from 'immer'
import { actionTypes } from '../actions/userProfiling'
import { actionTypes as documentActionTypes } from '../../routes/Documents/modules/DocumentsActions'
import { actionTypes as shopPickerActionTypes } from '../actions/shopPicker'
import { actionTypes as hActionTypes } from '../../routes/Homepage/modules/HomepageActions'
import { actionTypes as personalDataActionTypes } from '../../routes/PersonalData/modules/PersonalDataActions'
import { nullSafe } from 'Utility/globalMethods'

const ACTION_HANDLERS = {
  [actionTypes.GET_TAB_BAR_SUCCESS]: produce((draft, action) => {
    draft.nabBarItems = nullSafe(() => action.response.data.tabBar, [])
  }),
  [actionTypes.PROFILING_SUCCESS]: produce((draft, action) => {
    draft.profiling = nullSafe(() => action.response.data, [])
    draft.hashedMerchantId = nullSafe(() => action.response.data.hashedMerchantId, '')
  }),
  [actionTypes.DO_OPEN_MENU]: produce((draft, action) => {
    draft.openMenu = nullSafe(() => action.payload, '')
  }),
  [actionTypes.GET_NAVIGATION_PROFILE_SUCCESS]: produce((draft, action) => {
    draft.profileItemsData = {
      ...nullSafe(() => action.response.widgets, {})
    }
  }),
  [actionTypes.GET_NAVIGATION_PROFILE_FAILURE]: produce((draft, action) => {
    draft.errorData = {
      profile: nullSafe(() => action.error.data.errorCode, '')
    }
  }),
  [actionTypes.GET_NAVIGATION_MENU_SUCCESS]: produce((draft, action) => {
    draft.menuItemsData = {
      ...nullSafe(() => action.response.widgets, {})
    }
  }),
  [actionTypes.GET_NAVIGATION_MENU_FAILURE]: produce((draft, action) => {
    draft.errorData = {
      profile: nullSafe(() => action.error.data.errorCode, '')
    }
  }),
  [actionTypes.GET_NEW_MESSAGES_SUCCESS]: produce((draft, action) => {
    draft.newMessages = nullSafe(() => action.response.newMessage, 0)
  }),
  [actionTypes.GET_AML_STATUS_SUCCESS]: produce((draft, action) => {
    draft.amlStatusInfo = nullSafe(() => action.response)
  }),
  [actionTypes.GET_NEW_DOCUMENTS_SUCCESS]: produce((draft, action) => {
    draft.totalDocumentsCount = nullSafe(() => action.response.totalCounts, 0)
  }),
  [documentActionTypes.GET_NEW_DOCUMENTS_SUCCESS]: produce((draft, action) => {
    draft.totalDocumentsCount = nullSafe(() => action.response.totalCounts, 0)
  }),
  [documentActionTypes.DO_GET_DOWNLOAD]: produce((draft, action) => {
    if (draft.totalDocumentsCount > 0) {
      draft.totalDocumentsCount -= 1
    }
  }),
  [shopPickerActionTypes.GET_SHOP_LIST_REQUEST]: produce((draft) => {
    draft.shopListLoading = true
  }),
  [shopPickerActionTypes.GET_COSTS_SHOP_LIST_REQUEST]: produce((draft) => {
    draft.shopListCostsLoading = true
  }),
  [shopPickerActionTypes.GET_DISPUTE_SHOP_LIST_REQUEST]: produce((draft) => {
    draft.shopListDisputeLoading = true
  }),
  [shopPickerActionTypes.GET_DISPUTE_SHOP_LIST_SUCCESS]: produce((draft, action) => {
    draft.selectorDisputeGroups = nullSafe(() => action.response.selectorGroups, [])
    draft.shopListDisputeLoading = false
  }),
  [shopPickerActionTypes.GET_DISPUTE_SHOP_LIST_FAILURE]: produce((draft) => {
    draft.shopListDisputeLoading = false
  }),
  [shopPickerActionTypes.GET_SHOP_LIST_SUCCESS]: produce((draft, action) => {
    const selectorGroups = nullSafe(() => action.response.selectorGroups, [])

    draft.selectorGroups = selectorGroups
    draft.shopListLoading = false

    const selectedShops = selectorGroups.flatMap((group) =>
      group.shopsList.flatMap((parent) => {
        const hasChildren = Array.isArray(parent.shops) && parent.shops.length > 0

        if (hasChildren) {
          // Seleziona solo i figli selezionati
          return parent.shops
            .filter((shop) => shop.isSelected)
            .map((shop) => ({
              name: shop.labelTop || parent.title,
              typology: shop.childrenTypologyLabel || '',
              code: shop.code,
              address: shop.address
            }))
        } else {
          // Se il parent non ha figli e lui stesso è selezionato
          return parent.isSelected
            ? [
                {
                  name: parent.title,
                  typology: '', // puoi aggiungere se serve
                  code: parent.code,
                  address: parent.address
                }
              ]
            : []
        }
      })
    )
    // FULL LIST
    draft.listOfShops = selectedShops
  }),
  [shopPickerActionTypes.GET_COSTS_SHOP_LIST_SUCCESS]: produce((draft, action) => {
    const selectorGroups = nullSafe(() => action.response.selectorGroups, [])

    draft.selectorCostsGroups = selectorGroups
    draft.shopListCostsLoading = false

    const selectedShops = selectorGroups.flatMap((group) =>
      group.shopsList.flatMap((parent) => {
        const hasChildren = Array.isArray(parent.shops) && parent.shops.length > 0

        if (hasChildren) {
          // Seleziona solo i figli selezionati
          return parent.shops
            .filter((shop) => shop.isSelected)
            .map((shop) => ({
              name: shop.labelTop || parent.title,
              typology: shop.childrenTypologyLabel || '',
              code: shop.code,
              address: shop.address
            }))
        } else {
          // Se il parent non ha figli e lui stesso è selezionato
          return parent.isSelected
            ? [
                {
                  name: parent.title,
                  typology: '', // puoi aggiungere se serve
                  code: parent.code,
                  address: parent.address
                }
              ]
            : []
        }
      })
    )
    // FULL LIST
    draft.listOfCostsShops = selectedShops
    // PRESENCE FLAGS: indica quali gruppi sono presenti nella response
    const presence = {
      isNexi: false,
      isNexiDirect: false,
      isIsp: false,
      isOther: false
    }

    selectorGroups.forEach((g, index) => {
      const key = (g.group || '').toString().trim()
      if (key === 'NEXI') {
        presence.isNexi = true
      }
      if (key === 'NEXI_DIRECT') {
        presence.isNexiDirect = true
      }
      if (key === 'INTESA_SANPAOLO') {
        presence.isIsp = true
      }
      if (key === 'OTHER') {
        presence.isOther = true
      }
    })

    // derived flags
    presence.onlyNdOrIsp = !presence.isNexi && !presence.isOther && (presence.isNexiDirect || presence.isIsp)
    presence.onlyOther = presence.isOther && !presence.isNexi && !presence.isNexiDirect && !presence.isIsp

    draft.selectorCostsGroupsPresence = presence
  }),
  [shopPickerActionTypes.GET_SHOP_LIST_FAILURE]: produce((draft) => {
    draft.shopListLoading = false
  }),
  [shopPickerActionTypes.GET_COSTS_SHOP_LIST_FAILURE]: produce((draft) => {
    draft.shopListCostsLoading = false
  }),
  [hActionTypes.GET_SERVICE_QUICK_LINKS_SUCCESS]: produce((draft, action) => {
    draft.servicesQuickLinksData = nullSafe(() => action.response, [])
  }),
  [actionTypes.GET_RESULTS_GRAPH_VIEWED_SUCCESS]: produce((draft, action) => {
    draft.resultsGraphViewed = nullSafe(() => action.response.data, {})
  }),
  [actionTypes.DO_UPDATE_PHONE_FROM_SERVICES]: produce((draft, action) => {
    draft.profiling.user.telephoneNumber = nullSafe(() => action.payload.phone, draft.profiling.user.telephoneNumber)
    draft.profiling.user.prefix = nullSafe(() => action.payload.prefix, draft.profiling.user.prefix)
  }),
  [hActionTypes.GET_CONSENTS_LIST_SUCCESS]: produce((draft, action) => {
    draft.profiling.consents = nullSafe(() => action.response, {})
  }),
  [actionTypes.GET_SERVICE_QUICK_LINKS_SUCCESS]: produce((draft, action) => {
    draft.serviceQuickLinksData = nullSafe(() => action.response, {})
  }),
  [personalDataActionTypes.GET_PROFILE_DATA_SUCCESS]: produce((draft, action) => {
    draft.profiling.user.firstName = nullSafe(() => action.response.name, draft.profiling.user.firstName)
    draft.profiling.user.lastName = nullSafe(() => action.response.surname, draft.profiling.user.lastName)
  })
  // @generator reducer:type:action
}

// The initial state of the userProfiling
const initialState = {
  errorData: {},
  navBarItems: [],
  profiling: {},
  openMenu: '',
  profileItemsData: {},
  menuItemsData: {},
  newMessages: 0,
  totalDocumentsCount: 0,
  selectorGroups: [],
  selectorCostsGroups: [],
  selectorDisputeGroups: [],
  // Presence flags for costs selector groups
  selectorCostsGroupsPresence: {
    isNexi: false,
    isNexiDirect: false,
    isIsp: false,
    isOther: false,
    onlyNdOrIsp: false,
    onlyOther: false
  },
  shopListLoading: false,
  shopListCostsLoading: false,
  listOfShops: [],
  listOfCostsShops: [],
  servicesQuickLinksData: {},
  resultsGraphViewed: {},
  hashedMerchantId: '',
  serviceQuickLinksData: {}
}

const userProfiling = (state = initialState, action) => {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

export default userProfiling
